import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { colors } from '~/styles/colors';
import {
  DesktopOnly, MobileOnly, mobileQuery, PrimaryButton, SecondaryButton,
} from '../ds';
import { DownloadIcon, SaveIcon } from './icons';

const Bar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  border: 1px solid ${colors.stroke['soft-200']};
  border-radius: 12px;
  background: ${colors.bg['white-0']};
  box-shadow: 0 -2px 10px ${colors.celsius['shadow-card']};

  ${mobileQuery} {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px 14px;
  }
`;

const Notice = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: ${colors.text['sub-350']};

  & strong {
    color: ${colors.text['sub-500']};
  }

  ${mobileQuery} {
    font-size: 12px;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;

  ${mobileQuery} {
    flex-wrap: wrap-reverse;
    width: 100%;
    gap: 10px;
    margin-left: 0;

    /* Sem quebra de texto: se os dois botões não cabem lado a lado, cada um ocupa a linha inteira (Salvar em cima). */
    & > button {
      flex: 1 1 auto;
      white-space: nowrap;
    }
  }
`;

interface SaveBarProps {
  isSaving: boolean;
  onDiscard: () => void;
}

/** Barra do design que aparece só com alterações não salvas: Descartar e Salvar Configuração (um único comando). */
export function SaveBar({ isSaving, onDiscard }: Readonly<SaveBarProps>): ReactElement {
  const { t } = useTranslation();

  return (
    <Bar data-testid="eco-simplificado-barra-salvar">
      <DownloadIcon color={colors.text['sub-350']} strokeWidth={1.9} />
      <Notice data-testid="eco-simplificado-alteracoes-nao-salvas">
        <DesktopOnly as="span">
          <Trans i18nKey="ecoSimplificadoAlteracoesNaoSalvasComando" components={{ strong: <strong /> }} />
        </DesktopOnly>
        <MobileOnly as="span">
          <Trans i18nKey="ecoSimplificadoAlteracoesNaoSalvasComandoCurto" components={{ strong: <strong /> }} />
        </MobileOnly>
      </Notice>
      <Actions>
        <SecondaryButton type="button" disabled={isSaving} onClick={onDiscard} data-testid="eco-simplificado-descartar">
          {t('ecoSimplificadoDescartar')}
        </SecondaryButton>
        <PrimaryButton type="submit" disabled={isSaving} data-testid="eco-simplificado-salvar">
          <SaveIcon size={17} />
          {t('ecoSimplificadoSalvarConfiguracao')}
        </PrimaryButton>
      </Actions>
    </Bar>
  );
}
